import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader2, Copy, Check } from 'lucide-react';

interface Props {
  onAuth?: () => void;
}

export default function StorageCallbackPage({ onAuth }: Props) {
  const [state, setState] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleConnected = params.get('google');
    const onedriveConnected = params.get('onedrive');
    const zohoConnected = params.get('zoho');
    const t = params.get('token');
    const error = params.get('error');

    if (t) {
      localStorage.setItem('token', t);
      setToken(t);
      onAuth?.();
    }

    if (error) {
      setState('error');
      setMessage(decodeURIComponent(error).replace(/_/g, ' '));
    } else if (googleConnected === 'connected') {
      setState('success');
      setMessage('Google Drive connected successfully.');
    } else if (onedriveConnected === 'connected') {
      setState('success');
      setMessage('Microsoft OneDrive connected successfully.');
    } else if (zohoConnected === 'connected') {
      setState('success');
      setMessage('Zoho WorkDrive connected successfully.');
    } else {
      setState('error');
      setMessage('Unknown callback. Please try connecting again.');
    }

    // Clean up URL
    window.history.replaceState({}, '', '/storage/callback');
  }, []);

  function copyToken() {
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 max-w-lg w-full text-center space-y-4">
        {state === 'loading' && <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto" />}
        {state === 'success' && <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />}
        {state === 'error' && <XCircle className="w-10 h-10 text-red-500 mx-auto" />}

        <p className="text-sm text-gray-700 font-medium">{message || 'Processing…'}</p>

        {state === 'success' && token && (
          <div className="text-left space-y-2 pt-2">
            <p className="text-xs text-gray-500 font-medium">JWT Token — copy and paste into cosecoffice.com:</p>
            <div className="relative">
              <textarea
                readOnly
                value={token}
                rows={4}
                className="w-full text-xs font-mono bg-gray-50 border border-gray-200 rounded-lg p-3 pr-10 resize-none focus:outline-none text-gray-700"
              />
              <button
                onClick={copyToken}
                className="absolute top-2 right-2 p-1.5 rounded-md bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                title="Copy token"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
              </button>
            </div>
            <p className="text-xs text-gray-400">
              In cosecoffice.com browser console run:<br />
              <code className="bg-gray-100 px-1 rounded">localStorage.setItem('cosecdrive_token', '&lt;paste&gt;')</code>
            </p>
            <button
              onClick={() => { window.history.replaceState({}, '', '/'); window.location.href = '/'; }}
              className="w-full mt-2 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Go to Drive →
            </button>
          </div>
        )}

        {state === 'error' && (
          <button
            onClick={() => window.location.href = '/'}
            className="text-sm text-indigo-600 hover:underline"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
